<template>
	<div class="captcha-container">
		<div class="captcha-wrapper">
			<canvas ref="captchaCanvas" class="captcha-canvas" @click="refreshCaptcha"></canvas>
			<button type="button" class="refresh-captcha" @click="refreshCaptcha" :title="$t('captcha.refresh')">
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M23 4V10H17" stroke="currentColor" stroke-width="2" stroke-linecap="round"
						stroke-linejoin="round" />
					<path d="M1 20V14H7" stroke="currentColor" stroke-width="2" stroke-linecap="round"
						stroke-linejoin="round" />
					<path
						d="M3.51 9C4.15306 7.31727 5.33383 5.88822 6.85 4.925L3 2M20.5 15C19.8569 16.6827 18.6762 18.1118 17.16 19.075L21 22"
						stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
					<path
						d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
						stroke="currentColor" stroke-width="2" />
				</svg>
			</button>
		</div>
		<div class="captcha-input-wrapper" v-if="showInput">
			<input v-model="inputCaptcha" type="text" :placeholder="placeholder || $t('captcha.placeholder')"
				class="captcha-input" :maxlength="captchaLength" autocomplete="off" @input="onInput" />
		</div>
	</div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'

export default {
	name: 'CaptchaComponent',
	props: {
		// 验证码长度，默认为4位
		captchaLength: {
			type: Number,
			default: 4
		},
		// 是否显示输入框
		showInput: {
			type: Boolean,
			default: true
		},
		// 输入框占位符
		placeholder: {
			type: String,
			default: ''
		},
		// 是否自动刷新验证码
		autoRefresh: {
			type: Boolean,
			default: true
		},
		// 容器宽度
		width: {
			type: [Number, String],
			default: 120
		},
		// 容器高度
		height: {
			type: [Number, String],
			default: 40
		}
	},
	emits: ['update:modelValue', 'change', 'refresh'],
	setup(props, { emit }) {
		const captchaCanvas = ref(null)
		const inputCaptcha = ref('')
		const captchaText = ref('')

		// 生成随机验证码文本
		const generateCaptchaText = () => {
			const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
			let result = ''
			for (let i = 0; i < props.captchaLength; i++) {
				result += chars.charAt(Math.floor(Math.random() * chars.length))
			}
			return result
		}

		// 绘制验证码
		const drawCaptcha = () => {
			const canvas = captchaCanvas.value
			if (!canvas) return

			const ctx = canvas.getContext('2d')

			// 设置画布尺寸
			canvas.width = typeof props.width === 'number' ? props.width : parseInt(props.width)
			canvas.height = typeof props.height === 'number' ? props.height : parseInt(props.height)

			// 清空画布
			ctx.clearRect(0, 0, canvas.width, canvas.height)

			// 设置背景色
			ctx.fillStyle = '#f0f0f0'
			ctx.fillRect(0, 0, canvas.width, canvas.height)

			// 生成新的验证码文本
			captchaText.value = generateCaptchaText()

			// 设置文字样式
			ctx.font = `${canvas.height * 0.5}px Arial`
			ctx.fillStyle = '#333'

			// 绘制文字居中
			const textWidth = ctx.measureText(captchaText.value).width
			const textX = (canvas.width - textWidth) / 2
			const textY = canvas.height / 2 + 10
			ctx.fillText(captchaText.value, textX, textY)

			// 绘制干扰线
			for (let i = 0; i < 5; i++) {
				ctx.beginPath()
				ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height)
				ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height)
				ctx.strokeStyle = `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.5)`
				ctx.stroke()
			}

			// 绘制干扰点
			for (let i = 0; i < 30; i++) {
				ctx.beginPath()
				ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1, 0, 2 * Math.PI)
				ctx.fillStyle = `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.7)`
				ctx.fill()
			}

			// 触发刷新事件
			emit('refresh', captchaText.value)
		}

		// 刷新验证码
		const refreshCaptcha = () => {
			inputCaptcha.value = ''
			drawCaptcha()
		}

		// 输入事件处理
		const onInput = () => {
			emit('update:modelValue', inputCaptcha.value)
			emit('change', inputCaptcha.value)
		}

		// 验证输入的验证码是否正确
		const validate = (input) => {
			return input.toLowerCase() === captchaText.value.toLowerCase()
		}

		// 获取当前验证码文本
		const getCaptchaText = () => {
			return captchaText.value
		}

		// 监听尺寸变化
		watch([() => props.width, () => props.height], () => {
			drawCaptcha()
		})

		// 组件挂载后绘制验证码
		onMounted(() => {
			drawCaptcha()
		})

		return {
			captchaCanvas,
			inputCaptcha,
			refreshCaptcha,
			onInput,
			validate,
			getCaptchaText
		}
	}
}
</script>

<style scoped>
.captcha-container {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.captcha-wrapper {
	display: flex;
	align-items: center;
	gap: 8px;
}

.captcha-canvas {
	border: 1px solid #ddd;
	border-radius: 4px;
	cursor: pointer;
}

.refresh-captcha {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px;
	border: 1px solid #ddd;
	background: #f8f9fa;
	border-radius: 4px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.refresh-captcha:hover {
	background: #e9ecef;
}

.captcha-input-wrapper {
	width: 100%;
}

.captcha-input {
	width: 100%;
	padding: 14px;
	border: 1px solid #ddd;
	border-radius: 8px;
	font-size: 16px;
	box-sizing: border-box;
	transition: border-color 0.3s ease;
}

.captcha-input:focus {
	outline: none;
	border-color: #409eff;
	box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}
</style>